import React, { useCallback, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Copy, TriangleAlert } from "lucide-react"
import { Profile } from "@/reducers/profilesSlice"
import { Trans, useTranslation } from "react-i18next"

interface SetUserDisabledModalProps {
  isOpen: boolean
  onClose: () => void
  /** The user to disable or re-enable. */
  user: Profile
  /** If true, this action re-enables the user; otherwise it disables them. */
  enabling: boolean
  onSuccess: () => void
}

type Stage = "confirm" | "done"

export const SetUserDisabledModal: React.FC<SetUserDisabledModalProps> = ({
  isOpen,
  onClose,
  user,
  enabling,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [stage, setStage] = useState<Stage>("confirm")
  const [submitting, setSubmitting] = useState(false)
  const [newPassword, setNewPassword] = useState<string | undefined>(undefined)
  const [copied, setCopied] = useState(false)

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.name

  const handleClose = useCallback(() => {
    setStage("confirm")
    setNewPassword(undefined)
    setCopied(false)
    onClose()
  }, [onClose])

  const handleDone = useCallback(() => {
    handleClose()
    onSuccess()
  }, [handleClose, onSuccess])

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      const result = await AdHocCommands.setUserDisabled(
        window.XMPP!,
        user.jid,
        !enabling,
      )
      if (enabling) {
        setNewPassword(result.newPassword)
        setStage("done")
      } else {
        handleDone()
      }
    } catch (e: any) {
      const errorText = e?.text ?? e?.message ?? t("fleet.form.unknownError")
      window.appToaster.show({
        message: errorText,
        intent: "danger",
        timeout: 3000,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopy = useCallback(() => {
    if (!newPassword) return
    navigator.clipboard.writeText(newPassword).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [newPassword])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={stage === "done" ? undefined : handleClose}
    >
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        {stage === "confirm" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {enabling
                  ? t("fleet.setUserDisabled.titleEnable")
                  : t("fleet.setUserDisabled.titleDisable")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                {enabling ? (
                  <span>
                    <Trans
                      i18nKey="fleet.setUserDisabled.enableWarning"
                      values={{ name: displayName }}
                      components={{ b: <strong /> }}
                    />
                  </span>
                ) : (
                  <span>
                    <Trans
                      i18nKey="fleet.setUserDisabled.disableWarning"
                      values={{ name: displayName }}
                      components={{ b: <strong /> }}
                    />
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey={
                    enabling
                      ? "fleet.setUserDisabled.enableConfirm"
                      : "fleet.setUserDisabled.disableConfirm"
                  }
                  values={{ jid: user.jid }}
                  components={{
                    jid: <span className="font-medium text-foreground" />,
                  }}
                />
              </p>
            </div>

            <DialogFooter className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={submitting}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant={enabling ? "default" : "destructive"}
                onClick={handleConfirm}
                disabled={submitting}
                className={enabling ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                {enabling
                  ? t("fleet.setUserDisabled.enable")
                  : t("fleet.setUserDisabled.disable")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {t("fleet.setUserDisabled.enabledTitle")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("fleet.password.onlyOnce")}</span>
              </div>

              <div className="space-y-2">
                <Label>
                  {t("fleet.setUserDisabled.newPasswordFor", {
                    name: displayName,
                  })}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={newPassword ?? ""}
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopy}
                    title={t("fleet.password.copyToClipboard")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                {copied && (
                  <p className="text-xs text-muted-foreground">
                    {t("fleet.password.copied")}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={handleDone}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t("common.done")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SetUserDisabledModal
